import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useMutation } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';

import DocumentLink from 'components/shared/DocumentLink';
import DocumentTypeMenu from 'components/shared/DocumentTypeMenu';
import Tag from 'components/shared/Tag';
import { dateAtTime, simpleDate } from 'lib/date';
import Attachment from 'models/Attachment';
import { updateAttachment } from 'services/api/attachments';
import colors from 'styles/theme/colors';
import { BodySmallBold } from 'styles/typography';

const getAssociatedToColumnValues = (umAiSummaryEnabled: boolean, referralAiSummaryEnabled: boolean, value: any) => {
  const values: string[] = [];

  const authorization = umAiSummaryEnabled && value.latestAuthorizationReview;
  const referral = referralAiSummaryEnabled && value.isAssociatedToReferral;

  if (authorization) {
    const approvedDate = simpleDate(authorization.approvedThroughDate);
    const type = authorization.typeDisplayName;
    values.push([approvedDate, type].filter(Boolean).join(' '));
  }

  if (referral) {
    values.push('Referral');
  }

  return values;
};

const columnHelper = createColumnHelper<Attachment>();

export default function columns(umAiSummaryEnabled = false, referralAiSummaryEnabled = false) {
  return [
    columnHelper.accessor((row) => row.filename, {
      header: 'File Name',
      id: 'filename',
      cell: (info: any) => (
        <DocumentLink entry={info.row.original}>
          <BodySmallBold className='first-column' color={colors.primaryBlue}>
            {info.getValue() || `Untitled ${info.row.original.contentType} ${info.row.index}`}
          </BodySmallBold>
        </DocumentLink>
      ),
    }),
    columnHelper.accessor(
      (row) => ({ userFullName: row.createdByUser?.fullName, groupName: row.createdByGroup?.name }),
      {
        id: 'createdByUser',
        header: 'Uploaded By',
        cell: (info) => (
          <div>
            {info.getValue().userFullName} {info.getValue().groupName && `at ${info.getValue().groupName}`}
          </div>
        ),
      }
    ),
    columnHelper.accessor((row) => row.createdAt, {
      header: 'Uploaded On',
      id: 'createdAt',
      cell: (info) => (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>{dateAtTime(info.getValue())}</div>
          </div>
        </div>
      ),
    }),
    ...(umAiSummaryEnabled || referralAiSummaryEnabled
      ? [
          columnHelper.accessor((row) => row, {
            id: 'associatedTo',
            header: 'Associated To',
            cell: (info) => {
              const values = getAssociatedToColumnValues(umAiSummaryEnabled, referralAiSummaryEnabled, info.getValue());

              return (
                <AssociatedToColumn>
                  {values.length ? values.map((value) => <Tag label={value} />) : '—'}
                </AssociatedToColumn>
              );
            },
            enableSorting: false,
          }),
        ]
      : []),
    columnHelper.accessor((row) => row.docType, {
      id: 'docType',
      header: 'Document Type',
      cell: function Cell({ row }: { row: any }) {
        const { mutate } = useMutation({
          mutationFn: updateAttachment,
        });

        const [activeDocType, setDocType] = useState(row.original?.docType);

        useEffect(() => {
          setDocType(row.original?.docType);
        }, [row.original?.docType]);

        const changeDocType = (docType: string | null) => {
          mutate(
            { id: row.original.id, docType },
            {
              onSuccess: () => {
                row.original.docType = docType;
                setDocType(docType);
              },
            }
          );
        };

        return (
          <DocumentTypeContainer className='last-column'>
            <DocumentTypeMenu
              activeDocType={activeDocType}
              setActiveDocType={changeDocType}
              disabled={false}
              reportAbsentDoctype={true}
            />
          </DocumentTypeContainer>
        );
      },
    }),
  ];
}

const DocumentTypeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 16px;
`;

const AssociatedToColumn = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
